<template>
  <div style="position:absolute;top:0;left:0;right:0;bottom:0;" class="layout-column">
    <div class="layout-column flex ma-2 elevation-5"
         style="border-radius:4px;position:relative;"
         v-bind:style="{background: compColor.contentFrame}">

      <ValidationObserver ref="EncryptionKeyDetailForm" v-slot="{ valid }" class="layout-column flex">

        <div v-bind:style="{'z-index': '4'}" class="layout-row layout-align-center-center pa-1">
          <div class="layout-row">
            <lx-btn small classes="mr-1"
                    @click="back(true)"
                    icon="chevron_left"
                    :tooltip="$t('ENCRYPTION_KEYS.BUTTONS.BACK')"
                    hide-label>
            </lx-btn>
            <lx-btn small classes="mr-1"
                    @click="saveData"
                    icon="save"
                    :tooltip="$t('ENCRYPTION_KEYS.BUTTONS.SAVE')"
                    :disabled="isPristine || !valid"
                    hide-label>
            </lx-btn>
          </div>
          <div class="flex layout-row"></div>
        </div>

        <v-divider v-bind:style="{'border-color':compColor.lightDivider}"></v-divider>

        <div class="flex layout-column pa-2" style="overflow-y: auto;">
          <v-progress-linear
            v-if="loading"
            :indeterminate="loading"
            color="red accent-2"
            background-color="red lighten-4"
            class="ma-0"
            style="border-radius:0;height:4px;position: absolute;top:56px;left:0; width:100%;">
          </v-progress-linear>

          <lx-expansion-panels :single-open="false" v-model="panel">
            <lx-expansion-panel disabled>
              <lx-expansion-panel-header
                icon="mdi-key-variant"
                disabled
                :main-headline="$t('ENCRYPTION_KEYS.DETAILS.HEADER')"
                :sub-headline="$t('ENCRYPTION_KEYS.DETAILS.SUBHEADING')">
              </lx-expansion-panel-header>

              <lx-expansion-panel-content>
                <div class="flex pa-2 layout-column"
                     style="overflow-y: hidden;"
                     v-bind:class="[compColor.contentFrameBack]">
                  <div class="flex pa-3">
                    <div class="mb-4">
                      <validation-provider ref="name"
                                           tag="div"
                                           name="name"
                                           rules="required"
                                           v-slot="{ errors }">
                        <v-text-field
                          :dark="compColor.isInDarkMode"
                          :color="compColor.primeColor"
                          :label="$t('ENCRYPTION_KEYS.DETAILS.FIELDS.NAME') + '*'"
                          placeholder=" "
                          :dense="denseFields"
                          outlined
                          clearable
                          :error-messages="errors[0]"
                          :hide-details="!errors || errors.length <= 0"
                          v-model="model.name"
                          class="mb-3"
                        ></v-text-field>
                      </validation-provider>
                    </div>

                    <div class="mb-4">
                      <validation-provider ref="key"
                                           tag="div"
                                           name="key"
                                           rules="required|min:4|max:4"
                                           v-slot="{ errors }">
                        <v-text-field
                          :dark="compColor.isInDarkMode"
                          :color="compColor.primeColor"
                          :label="$t('ENCRYPTION_KEYS.DETAILS.FIELDS.KEY') + '*'"
                          maxlength="4"
                          placeholder=" "
                          :dense="denseFields"
                          outlined
                          clearable
                          :error-messages="errors[0]"
                          :hide-details="!errors || errors.length <= 0"
                          v-model="model.key"
                          class="mb-3"
                        ></v-text-field>
                      </validation-provider>
                    </div>

                    <div class="layout-row mb-4" v-if="!model._id || model._id && model.privateKey.name === null">
                      <v-btn
                        @click="openHiddenFileInput()"
                        :color="compColor.primeColor"
                        :dark="compColor.isInDarkMode"
                        :width="300"
                        :height="denseFields ? 39 : 48"
                        class="white--text text-none pa-4">
                        {{ $t('ENCRYPTION_KEYS.DETAILS.UPLOAD_BUTTON') }}
                      </v-btn>
                    </div>

                    <div class="layout-colum blue-grey--text font-weight-regular subtitle-2" v-if="model.privateKey.name !== null">
                      <span>{{ $t('ENCRYPTION_KEYS.DETAILS.KEY_DETAILS.TITLE') }}</span>
                      <div class="layout-row">
                        <span class="ml-5 flex-10">{{ $t('ENCRYPTION_KEYS.DETAILS.KEY_DETAILS.NAME') + ': ' }}</span>
                        <span>{{ model.privateKey.name }}</span>
                      </div>
                      <div class="layout-row">
                        <span class="ml-5 flex-10">{{ $t('ENCRYPTION_KEYS.DETAILS.KEY_DETAILS.DATE') + ': ' }}</span>
                        <span>{{ model.privateKey.lastModified | formatDate }}</span>
                      </div>
                      <div class="layout-row">
                        <span class="ml-5 flex-10">{{ $t('ENCRYPTION_KEYS.DETAILS.KEY_DETAILS.TYPE') + ': ' }}</span>
                        <span>{{ model.privateKey.type }}</span>
                      </div>
                    </div>

                    <div class="layout-row">
                      <v-switch
                        v-model="model.isActive"
                        class="ml-2 mb-4 my-0 py-0"
                        :dense="denseFields"
                        inset
                        :color="compColor.prime"
                        :dark="isDarkMode"
                        hide-details
                      >
                        <template #label>
                          <span class="body-2 blue-grey--text text--lighten-2">{{ $t('ENCRYPTION_KEYS.DETAILS.IS_ACTIVE') }}</span>
                        </template>
                      </v-switch>
                    </div>

                    <input type="file" hidden ref="hiddenFileInput" @change="addFilesByDialog()"/>

                  </div>
                </div>
              </lx-expansion-panel-content>
            </lx-expansion-panel>
          </lx-expansion-panels>

        </div>
      </ValidationObserver>
    </div>
  </div>
</template>
<script>
import lodash from 'lodash'

export default {
  name: 'EncryptionKeyDetails',
  props: {
    encryptionKeyId: {
      type: String
    },
    files: []
  },

  data () {
    return {
      loading: false,
      model: {
        isActive: true,
        isEditable: true,
        privateKey: {
          name: null,
          lastModified: null,
          type: null,
          content: null
        }
      },
      oldModel: {},
      panel: [0]
    }
  },

  created () {
    if (this.encryptionKeyId) {
      this.getData()
    }
  },

  methods: {
    /**
     * Back to overview
     */
    back () {
      // Trigger 'setShowDetails()' on parent
      this.$emit('set-show-details', false)
    },

    /**
     * Get selected encryption key from db if exists
     */
    getData () {
      this.$http.get('/encryptionKeys/' + this.encryptionKeyId)
        .then(res => {
          if (res.data) {
            this.model = res.data || []
            this.oldModel = lodash.cloneDeep(this.model)
          }
        })
        .catch(this.NotifyError)
        .finally(() => {
          this.loading = false
        })
    },

    /**
     * Save selected news
     */
    saveData () {
      if (this.loading) return
      this.loading = true

      this.$http[!this.model._id ? 'post' : 'put']('/encryptionKeys', this.model)
        .then(() => {
          this.NotifySystem(this.$t('ENCRYPTION_KEYS.DETAILS.SUCCESSFULLY_SAVED'))

          // return to overview
          this.back(false)
        })
        .catch(error => {
          this.populateFormError(this.$refs.EncryptionKeyDetailForm, error).catch(this.NotifyError)
        })
        .finally(() => {
          this.loading = false
        })
    },

    /**
     * Open file chooser to select file to upload
     */
    openHiddenFileInput () {
      if (this.$refs.hiddenFileInput) {
        // reset file input um neue dateien hochladen zu können
        this.$refs.hiddenFileInput.value = ''
        this.$refs.hiddenFileInput.click()
      }
    },

    /**
     * wird bei onChange aufgerufen, wenn im Dateidialog Dateien ausgewählt werden
     * diese werden an Methode addFiles übergeben, wo sie zerlegt und aufbereitet werden
     */
    addFilesByDialog () {
      let file = this.$refs.hiddenFileInput

      if (file && file.files && file.files.length > 0) {
        let read = new FileReader()
        read.readAsBinaryString(file.files[0])

        read.onloadend = () => {
          this.model.privateKey = {
            name: file.files[0].name,
            lastModified: file.files[0].lastModifiedDate,
            type: file.files[0].type,
            content: read.result
          }
        }
      }
    }
  },

  computed: {
    isPristine () {
      if (!this.oldModel) {
        return false
      }
      return JSON.stringify(this.oldModel) === JSON.stringify(this.model)
    }
  }
}
</script>
